import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { surveyDataService } from '../../services/surveyDataService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface QuestionModalProps {
  open: boolean;
  onClose: () => void;
  isManager?: boolean;
  branchId?: string;
  /** Anket kaydedildikten sonra (liste yenileme) cagrilir. */
  onSaved?: () => void;
}

interface AnswerItem {
  text: string;
  type: 'positive' | 'negative' | 'neutral';
}

const QuestionModal: React.FC<QuestionModalProps> = ({ open, onClose, isManager = false, branchId, onSaved }) => {
  const { t } = useTranslation('common2');
  const { currentUser } = useAuth();
  const [questionTitle, setQuestionTitle] = useState('');
  const [answers, setAnswers] = useState<AnswerItem[]>([{ text: '', type: 'neutral' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleAddAnswer = () => {
    setAnswers([...answers, { text: '', type: 'neutral' }]);
  };

  const handleRemoveAnswer = (index: number) => {
    const newAnswers = answers.filter((_, i) => i !== index);
    setAnswers(newAnswers);
  };

  const handleAnswerTextChange = (index: number, value: string) => {
    const newAnswers = [...answers];
    newAnswers[index] = { ...newAnswers[index], text: value };
    setAnswers(newAnswers);
  };

  const handleAnswerTypeChange = (index: number, type: 'positive' | 'negative' | 'neutral') => {
    const newAnswers = [...answers];
    newAnswers[index] = { ...newAnswers[index], type };
    setAnswers(newAnswers);
  };

  const getTypeColor = (type: 'positive' | 'negative' | 'neutral') => {
    switch (type) {
      case 'positive': return 'success';
      case 'negative': return 'error';
      case 'neutral': return 'default';
    }
  };

  const getTypeLabel = (type: 'positive' | 'negative' | 'neutral') => {
    switch (type) {
      case 'positive': return t('surveys.edit.answerTypes.positive');
      case 'negative': return t('surveys.edit.answerTypes.negative');
      case 'neutral': return t('surveys.edit.answerTypes.neutral');
    }
  };

  const handleSaveQuestion = async () => {
    if (!questionTitle.trim()) {
      setError(t('surveys.questionModal.errors.emptyQuestion'));
      return;
    }

    if (answers.length === 0) {
      setError(t('surveys.questionModal.errors.noAnswers'));
      return;
    }

    if (answers.some(answer => !answer.text.trim())) {
      setError(t('surveys.questionModal.errors.emptyAnswer'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      if (!currentUser) {
        setError(t('surveys.questionModal.errors.noSession'));
        return;
      }

      // Anketi SQL'e kaydet (companyId X-Company-Id header'indan; yönetici modunda şube ID'si eklenir).
      await surveyDataService.createSurvey({
        title: questionTitle,
        answers,
        createdByUid: currentUser.uid,
        branchId: isManager && branchId ? branchId : null,
      });

      // Başarılı mesajı göster, listeyi yenile ve modalı kapat
      setSuccess(t('surveys.questionModal.success'));
      if (onSaved) onSaved();
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Anket eklenirken hata:', error);
      setError(t('surveys.questionModal.errors.addError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setQuestionTitle('');
    setAnswers([{ text: '', type: 'neutral' }]);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isManager && branchId ? t('surveys.questionModal.titleForBranch') : t('surveys.questionModal.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            label={t('surveys.questionModal.questionTitle')}
            value={questionTitle}
            onChange={(e) => setQuestionTitle(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {t('surveys.questionModal.answerOptions')}
          </Typography>

          {answers.map((answer, index) => (
            <Paper 
              key={index} 
              sx={{ 
                p: 2, 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label={`${t('surveys.questionModal.answer')} ${index + 1}`}
                  value={answer.text}
                  onChange={(e) => handleAnswerTextChange(index, e.target.value)}
                />
                
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>{t('surveys.questionModal.status')}</InputLabel>
                  <Select
                    value={answer.type}
                    label={t('surveys.questionModal.status')}
                    onChange={(e) => handleAnswerTypeChange(index, e.target.value as 'positive' | 'negative' | 'neutral')}
                    size="small"
                  >
                    <MenuItem value="positive">
                      <Chip label={t('surveys.edit.answerTypes.positive')} color="success" size="small" />
                    </MenuItem>
                    <MenuItem value="negative">
                      <Chip label={t('surveys.edit.answerTypes.negative')} color="error" size="small" />
                    </MenuItem>
                    <MenuItem value="neutral">
                      <Chip label={t('surveys.edit.answerTypes.neutral')} color="default" size="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
                
                <Chip 
                  label={getTypeLabel(answer.type)} 
                  color={getTypeColor(answer.type) as any}
                  size="small"
                />
                
                <IconButton 
                  color="error" 
                  onClick={() => handleRemoveAnswer(index)}
                  disabled={answers.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddAnswer}
            sx={{ mt: 1 }}
          >
            {t('surveys.questionModal.addAnswerOption')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('surveys.questionModal.actions.cancel')}
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSaveQuestion}
          disabled={isSubmitting || !questionTitle.trim() || answers.every(answer => !answer.text.trim())}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting ? t('surveys.questionModal.actions.saving') : t('surveys.questionModal.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionModal; 